import type { Messages } from "@/i18n/locales/en";
import { legalMessagesSl } from "@/i18n/locales/legalSlData";
import { pagesMessagesSl } from "@/i18n/locales/pagesSlData";

export const slMessages = {
  meta: {
    description:
      "WasOK — optično preberite izdelke, shranite ocene in zapiske, zapomnite si, kaj se je splačalo kupiti.",
    title: "WasOK — Zapomnite si, kaj se je splačalo kupiti",
  },
  header: {
    navFeatures: "Funkcije",
    navHowItWorks: "Kako deluje",
    navPrivacy: "Zasebnost",
    navUseCases: "Primeri uporabe",
    download: "Prenesi",
    openMenu: "Odpri meni",
    closeMenu: "Zapri meni",
    languageAria: "Jezik",
  },
  hero: {
    badge: "📱 Zdaj na voljo za Android",
    title: "Zapomnite si, kaj se je splačalo kupiti.",
    subtitle:
      "Optično preberite izdelke, shranite ocene in zapiske ter takoj vedite, ali je bil izdelek res dober.",
    googlePlay: "Prenesi iz trgovine Google Play",
    watchDemo: "Oglejte si predstavitev",
    privacyLine: "Vsi podatki ostanejo na vaši napravi.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Skenirajte, da si zapomnite",
    snackCard: "Odlično!",
    coffeeCard: "Preveč grenka",
    scrollHint: "Pomaknite se in raziskujte",
  },
  problems: {
    heading: "Se sliši znano?",
    subheading:
      "Vsi smo že bili tam — stojimo pred izdelki in poskušamo se spomniti, ali so nam bili prejšnjič všeč.",
    notedAgo: "✓ To ste zabeležili pred 3 meseci",
    items: [
      {
        question: "Katera čokolada mi je bila res všeč?",
        note: "Popolna sladkost",
      },
      {
        question: "Zakaj sem nehal kupovati ta šampon?",
        note: "Posušil mi je lase",
      },
      {
        question: "Je bila ta kava dobra ali razočaranje?",
        note: "Preveč grenka",
      },
    ],
  },
  howItWorks: {
    heading: "Kako deluje",
    subheading:
      "Štirje preprosti koraki, da nikoli več ne pozabite svojih mnenj o izdelkih.",
    stepPrefix: "Korak",
    steps: [
      {
        title: "Optično preberite katero koli črtno kodo izdelka",
        description:
          "Hitro in enostavno skeniranje s kamero. Deluje s katero koli črtno kodo.",
      },
      {
        title: "Shranite ocene, komentarje in fotografije",
        description:
          "Ocenite izdelke, dodajte zapiske, priložite fotografije ter zabeležite cene in lokacije.",
      },
      {
        title: "Takoj se spomnite svoje izkušnje",
        description:
          "Kasneje optično preberite isti izdelek in si oglejte prejšnjo oceno ter zapiske.",
      },
      {
        title: "Ustvarite pametne nakupovalne sezname",
        description:
          "Sestavite nakupovalne sezname iz izdelkov, ki jim že zaupate in jih imate radi.",
      },
    ],
  },
  privacy: {
    heading: "Vaša zgodovina nakupov ostane vaša.",
    body: "WasOK vam pomaga zapomniti izdelke, ki ste jih preizkusili, pri čemer ocene, zapiske, fotografije in nakupovalne sezname shrani lokalno na vaši napravi.",
    trustBanner:
      "💚 Zgrajeno okoli vaše zgodovine nakupov — ne okoli zbiranja vaših podatkov.",
    bullets: [
      "Račun ni potreben",
      "Sinhronizacija v oblaku ni potrebna",
      "Lokalna shramba",
      "Varnostne kopije pod nadzorom uporabnika",
      "Izbirno iskanje po črtni kodi za informacije o izdelku.",
      "Podprto z oglasi prek Google AdMob",
    ],
  },
  features: {
    heading: "Vse, kar potrebujete",
    subheading:
      "Zmogljive funkcije, zasnovane tako, da je sledenje izdelkom preprosto in zasebno.",
    items: [
      {
        title: "Bralnik črtnih kod",
        description: "Hitro in natančno skeniranje črtnih kod za kateri koli izdelek.",
      },
      {
        title: "Ocene izdelkov",
        description: "Ocenite izdelke z 1–5 zvezdicami, da si zapomnite kakovost.",
      },
      {
        title: "Fotografije izdelkov",
        description: "Priložite fotografije, da si zapomnite embalažo ali podrobnosti.",
      },
      {
        title: "Kategorije",
        description: "Organizirajte izdelke v lastne kategorije.",
      },
      {
        title: "Sledenje trgovinam",
        description: "Zapomnite si, kje ste kupili vsak izdelek.",
      },
      {
        title: "Beleženje cen",
        description: "Spremljajte cene in poiščite najboljše ponudbe.",
      },
      {
        title: "Nakupovalni seznami",
        description: "Sestavite sezname iz priljubljenih izdelkov.",
      },
      {
        title: "Dostop brez povezave",
        description: "Popolnoma deluje tudi brez internetne povezave.",
      },
      {
        title: "Lokalna shramba",
        description: "Vsi vaši podatki so varno shranjeni na vaši napravi.",
      },
    ],
  },
  useCases: {
    heading: "Popolno za vsako polico",
    subheading:
      "Ne glede na to, ali nakupujete hrano, kozmetiko ali gospodinjske izdelke — WasOK vam pomaga zapomniti.",
    cards: [
      {
        emoji: "🍫",
        title: "Prigrizki in slaščice",
        description: "Zapomnite si, katere sladkarije so bile res dobre.",
      },
      {
        emoji: "☕",
        title: "Kava in pijače",
        description: "Spremljajte kave, ki se jih splača znova kupiti.",
      },
      {
        emoji: "💄",
        title: "Kozmetika in nega kože",
        description: "Beležite reakcije kože.",
      },
      {
        emoji: "🧹",
        title: "Gospodinjski izdelki",
        description: "Izogibajte se ponovnemu nakupu izdelkov, ki so razočarali.",
      },
    ],
  },
  screenshots: {
    heading: "Lepo in intuitivno",
    subheading:
      "Premišljeno zasnovan vmesnik, zaradi katerega je sledenje izdelkom užitek.",
    items: [
      { title: "Bralnik" },
      { title: "Podrobnosti izdelka" },
      { title: "Vnos ocene" },
      { title: "Iskanje in zgodovina" },
      { title: "Nakupovalni seznam" },
    ],
  },
  cta: {
    heading: "Spomin za nakupovanje v resničnem življenju.",
    body: "Nehajte pozabljati, kateri izdelki so se splačali. Začnite nakupovati pametneje že danes.",
    googlePlay: "Prenesi iz trgovine Google Play",
    scanQr: "Skenirajte za prenos",
    tagline: "Zasebno · Brez povezave · Preprosto",
  },
  footer: {
    tagline: "Zapomnite si, kaj se je splačalo kupiti.",
    linksHeading: "Povezave",
    privacyPolicy: "Pravilnik o zasebnosti",
    terms: "Pogoji uporabe",
    contact: "Kontakt",
    linkFaq: "FAQ",
    linkLicenses: "Licence odprte kode",
    linkAdvertising: "Oglaševanje in monetizacija",
    downloadHeading: "Prenesi",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Vse pravice pridržane. Ustvarjeno z 💚 za pametnejše nakupovanje.",
  },
  legal: legalMessagesSl,
  faq: pagesMessagesSl.faq,
  support: pagesMessagesSl.support,
  advertising: pagesMessagesSl.advertising,
} satisfies Messages;
